/*
Bridges Clerk's session token into the plain (non-React) backend request client.

The shared request client (generic_backend_request_post) is a normal async
function, not a React component, so it can't call Clerk's useAuth() hook to read
the session token. Instead a component rendered under <ClerkProvider> (LoginSync)
registers Clerk's getToken here once via setAuthTokenGetter, and the client reads
the current session JWT through getAuthToken() on each request.

Two different callers, two different contracts:

  - **Signed out.** Returns null immediately and the request goes without a
    header. That is correct and permanent: the anonymous funnel (gallery,
    builder, cost matrix, solve, poll, results) is served by endpoints that
    never resolve a user, and it must not be slowed down waiting for a token
    that will never exist.

  - **Signed in.** The token is treated as required. Under AUTH_ENFORCE a
    request without one is a 401, so quietly falling through to an
    unauthenticated request would turn a slow Clerk response into an
    intermittent, near-undiagnosable auth failure for a legitimate user. We wait
    longer and retry rather than give up on the first slow call.

Still never throws and never blocks forever — an unbounded wait would stall the
page, which is worse than a failed request.
*/

let tokenGetter = null;
let signedIn = false;

// Requests can fire before LoginSync mounts (it lives in the Navbar, while page
// components fetch from their own effects). Before this existed, such a request
// saw no getter and silently went out unauthenticated — harmless in soft mode,
// a 401 under enforcement. Callers now wait briefly for registration instead of
// racing it.
let registered = false;
let resolveRegistered;
const registeredPromise = new Promise((resolve) => { resolveRegistered = resolve; });

/** Upper bound on waiting for LoginSync to register the getter. */
const REGISTRATION_TIMEOUT_MS = 3000;

/** Per-attempt bound on Clerk's getToken(). */
const TOKEN_TIMEOUT_MS = 2500;

/**
 * Attempts for a signed-in user. Clerk caches session tokens, so a retry costs
 * nothing in the normal case; it only matters on a cold or contended call.
 */
const SIGNED_IN_ATTEMPTS = 3;

/**
 * Registered by a component under <ClerkProvider> with Clerk's useAuth().getToken.
 *
 * @param {Function|null} getter - Clerk's getToken, or null to clear.
 * @param {boolean} [isSignedIn=false] - Whether Clerk reports an active session.
 *   Drives whether a missing token is acceptable (anonymous) or worth retrying.
 */
export function setAuthTokenGetter(getter, isSignedIn = false) {
  tokenGetter = typeof getter === 'function' ? getter : null;
  signedIn = Boolean(isSignedIn);
  if (!registered) {
    registered = true;
    resolveRegistered();
  }
}

/** Test seam: reset module state between cases. */
export function __resetAuthTokenForTests() {
  tokenGetter = null;
  signedIn = false;
}

function withTimeout(promise, ms) {
  let timer;
  return Promise.race([
    Promise.resolve().then(() => promise),
    new Promise((resolve) => { timer = setTimeout(() => resolve(null), ms); }),
  ]).finally(() => clearTimeout(timer));
}

async function attemptToken() {
  try {
    // Wrap so a synchronous throw in tokenGetter becomes a caught rejection.
    const token = await withTimeout(Promise.resolve().then(() => tokenGetter()), TOKEN_TIMEOUT_MS);
    return token || null;
  } catch (e) {
    return null;
  }
}

/**
 * Resolves to the current Clerk session JWT, or null when signed out / before
 * Clerk has registered a getter / on repeated failure. Never rejects.
 *
 * @returns {Promise<string|null>}
 */
export async function getAuthToken() {
  // Wait briefly for LoginSync rather than racing it. Resolves immediately once
  // registration has happened, which is the case for all but the first requests
  // of a page load.
  if (!registered) {
    await withTimeout(registeredPromise, REGISTRATION_TIMEOUT_MS);
  }

  if (!tokenGetter) return null;
  // Anonymous: no token exists, so don't spend time looking for one.
  if (!signedIn) return null;

  for (let attempt = 0; attempt < SIGNED_IN_ATTEMPTS; attempt += 1) {
    const token = await attemptToken();
    if (token) return token;
  }
  // Exhausted: proceed without the header rather than hanging the request. Under
  // enforcement this surfaces as a 401, which is the honest outcome — the
  // alternative is a page that never finishes loading.
  return null;
}
